/**
 * Host-page mutations the sheet layout makes, reference-counted at module
 * level (#856, codex r1 HIGH).
 *
 * The arm-guard (sheetOwner.ts) already keeps two sheets from being active
 * at once, but the HANDOVER between an unmounting owner and the waiter that
 * claims next runs inside one React commit, and React's unmount-cleanup
 * order decides whether the old owner's restore runs before or after the new
 * owner's lock. Snapshotting per instance would then let the new owner save
 * `overflow: hidden` as the "original" and leave the host page locked for
 * good. Counting acquisitions and restoring only when the LAST holder
 * releases makes the outcome independent of that order.
 *
 * Inline styles (not classes) because the widget's stylesheet lives in a
 * shadow root and cannot reach the host document or its ancestors.
 */

// Longhands, not the shorthands: a host inline `overflow-y: scroll !important`
// is invisible through the `overflow` shorthand, and setting/removing the
// shorthand would overwrite it (codex r3). Each longhand is snapshotted,
// locked and restored on its own.
const SCROLL_PROPS = ['overflow-x', 'overflow-y', 'overscroll-behavior-x', 'overscroll-behavior-y'] as const
type ScrollSaved = Array<readonly [el: HTMLElement, prop: (typeof SCROLL_PROPS)[number], value: string, priority: string]>

let scrollDepth = 0
let scrollSaved: ScrollSaved | null = null

/**
 * Page scroll lock while the sheet + map own the viewport (module css:15-19,
 * which is `!important` there too). Applied with `important` so a host rule
 * like `html { overflow-y: auto !important }` cannot defeat it, and the
 * snapshot carries each inline value's own priority so an existing
 * `!important` inline style is restored exactly (codex r2).
 */
export function acquireHostScrollLock(): () => void {
  if (typeof document === 'undefined') return () => {}
  const html = document.documentElement
  const body = document.body
  if (scrollDepth === 0) {
    const saved: ScrollSaved = []
    for (const el of [html, body]) {
      for (const prop of SCROLL_PROPS) {
        saved.push([el, prop, el.style.getPropertyValue(prop), el.style.getPropertyPriority(prop)] as const)
      }
      el.style.setProperty('overflow-x', 'hidden', 'important')
      el.style.setProperty('overflow-y', 'hidden', 'important')
      el.style.setProperty('overscroll-behavior-x', 'none', 'important')
      el.style.setProperty('overscroll-behavior-y', 'none', 'important')
    }
    scrollSaved = saved
    html.setAttribute('data-hfse-mres-on', '')
  }
  scrollDepth++
  let released = false
  return () => {
    if (released) return
    released = true
    scrollDepth--
    if (scrollDepth > 0 || !scrollSaved) return
    for (const [el, prop, value, priority] of scrollSaved) {
      if (value) el.style.setProperty(prop, value, priority)
      else el.style.removeProperty(prop)
    }
    html.removeAttribute('data-hfse-mres-on')
    scrollSaved = null
  }
}

/** Set on ancestors whose transform/filter/contain would trap the fixed sheet (js:279-303). */
export const UNTRANSFORM_ATTR = 'data-hfse-mres-untransform'
const TRAP_PROPS = ['transform', 'filter', 'perspective', 'will-change', 'contain'] as const
type SavedProp = readonly [prop: (typeof TRAP_PROPS)[number], value: string, priority: string]

let neutralized = new WeakMap<HTMLElement, { count: number; saved: SavedProp[] }>()

function trapsFixed(cs: CSSStyleDeclaration): boolean {
  return Boolean(
    (cs.transform && cs.transform !== 'none') ||
    (cs.filter && cs.filter !== 'none') ||
    (cs.perspective && cs.perspective !== 'none') ||
    (cs.willChange && /transform|perspective|filter/.test(cs.willChange)) ||
    (cs.contain && /strict|content|paint|layout/.test(cs.contain)),
  )
}

function acquireUntransform(element: HTMLElement): () => void {
  const entry = neutralized.get(element)
  if (entry) {
    entry.count++
  } else {
    const saved = TRAP_PROPS.map((p) => [p, element.style.getPropertyValue(p), element.style.getPropertyPriority(p)] as const)
    element.style.setProperty('transform', 'none', 'important')
    element.style.setProperty('filter', 'none', 'important')
    element.style.setProperty('perspective', 'none', 'important')
    element.style.setProperty('will-change', 'auto', 'important')
    element.style.setProperty('contain', 'none', 'important')
    element.setAttribute(UNTRANSFORM_ATTR, '')
    neutralized.set(element, { count: 1, saved })
  }
  let released = false
  return () => {
    if (released) return
    released = true
    const current = neutralized.get(element)
    if (!current) return
    current.count--
    if (current.count > 0) return
    for (const [p, value, priority] of current.saved) {
      if (value) element.style.setProperty(p, value, priority)
      else element.style.removeProperty(p)
    }
    element.removeAttribute(UNTRANSFORM_ATTR)
    neutralized.delete(element)
  }
}

/**
 * A transformed / filtered / contained ancestor makes `position: fixed`
 * resolve against it instead of the viewport, which strands the sheet
 * inside a page section. Starts at the element itself and walks up through
 * the shadow boundary via `.host`,
 * overrides inline with `!important`, and restores when the last holder
 * releases. Returns the release function.
 */
export function neutralizeFixedTraps(el: HTMLElement | null): () => void {
  if (!el || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return () => {}
  const releases: Array<() => void> = []
  // Start at the element ITSELF: a transform/filter/contain on the widget
  // root traps every fixed descendant just the same (codex fresh pass).
  let node: Node | null = el
  while (node && node !== document.documentElement) {
    if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      node = (node as ShadowRoot).host ?? null
      continue
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      node = node.parentNode
      continue
    }
    const element = node as HTMLElement
    // Already neutralized by another holder: count us in without re-reading
    // the (now overridden) computed style.
    if (neutralized.has(element) || trapsFixed(window.getComputedStyle(element))) {
      releases.push(acquireUntransform(element))
    }
    node = element.parentNode
  }
  return () => { for (const release of releases) release() }
}

/** Test hook: forget every host-page mutation record between test files. */
export function __resetHostPageForTests(): void {
  scrollDepth = 0
  scrollSaved = null
  // The untransform records and the arm attribute are part of the contract this
  // claims to reset. Leaving `neutralized` populated made a later
  // acquireUntransform() bump a stale counter whose release then never restored
  // the original transform/contain — a cross-file leak.
  neutralized = new WeakMap()
  if (typeof document !== 'undefined') {
    document.documentElement.removeAttribute('data-hfse-mres-on')
  }
}
